import { useEffect, useState } from "react";
import { Box, Button, IconButton, Typography, keyframes } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import { isPWAStandalone } from "@/utils/environment.utils";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "pwa-install-banner-dismissed";

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

/** iOS Share icon (square with arrow up) */
const IOSShareIcon: React.FC = () => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      animation: `${bounce} 1.5s ease-in-out infinite`,
      verticalAlign: "middle",
    }}
  >
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 2L8 6M12 2L16 6M12 2V15"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5 10V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V10"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </Box>
);

const PWAInstallBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (isPWAStandalone()) return;
    if (window.innerWidth > 768) return;

    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);

    if (isIOSDevice) {
      setShowBanner(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: "linear-gradient(135deg, #032324 0%, #032324 100%)",
        color: "#fff",
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
      }}
    >
      <Box
        component="img"
        src="/logo_balao_azul.png"
        alt="Talk2Buy"
        sx={{ width: 44, height: 44, borderRadius: 2, flexShrink: 0 }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>Instale a Talk2Buy</Typography>
        {isIOS ? (
          <Typography
            sx={{
              fontSize: 12,
              opacity: 0.85,
              lineHeight: 1.4,
              mt: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            Toque em <IOSShareIcon /> e depois <strong>"Adicionar à Tela de Início"</strong>
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, opacity: 0.85, lineHeight: 1.3, mt: 0.25 }}>
            Acesse mais rápido direto da sua tela inicial
          </Typography>
        )}
      </Box>

      {!isIOS && (
        <Button
          variant="contained"
          size="small"
          startIcon={<GetAppIcon />}
          onClick={handleInstall}
          sx={{
            bgcolor: "#fff",
            color: "#032324",
            fontWeight: 700,
            fontSize: 12,
            textTransform: "none",
            borderRadius: 2,
            px: 1.5,
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
          }}
        >
          Instalar
        </Button>
      )}

      <IconButton size="small" onClick={handleDismiss} sx={{ color: "#fff", opacity: 0.7, flexShrink: 0 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default PWAInstallBanner;
